import { useCallback } from "react";
import { useSetAtom } from "jotai";
import {
    addNotificationAtom,
    type NotificatonPosition,
} from "@components/Notification/store/notificationAtoms";
import { ComponentColor } from "@common/types";

export const useNotification = () => {
    const addNotification = useSetAtom(addNotificationAtom);

    const showNotification = useCallback(
        ({
            message,
            title,
            color = ComponentColor.Info,
            position = "top-center",
            interval = null,
        }: ShowNotificationOptions) => {
            addNotification({ message, title, color, position, interval });
        },
        [addNotification],
    );

    const showSuccess = useCallback(
        ({
            message,
            title,
            position = "top-center",
            interval = null,
        }: Omit<ShowNotificationOptions, "color">) => {
            addNotification({
                message,
                title,
                color: ComponentColor.Success,
                position,
                interval,
            });
        },
        [addNotification],
    );

    const showError = useCallback(
        ({
            message,
            title,
            position = "top-center",
            interval = null,
        }: Omit<ShowNotificationOptions, "color">) => {
            addNotification({
                message,
                title,
                color: ComponentColor.Error,
                position,
                interval,
            });
        },
        [addNotification],
    );

    const showWarning = useCallback(
        ({
            message,
            title,
            position = "top-center",
            interval = null,
        }: Omit<ShowNotificationOptions, "color">) => {
            addNotification({
                message,
                title,
                color: ComponentColor.Warning,
                position,
                interval,
            });
        },
        [addNotification],
    );

    const showInfo = useCallback(
        ({
            message,
            title,
            position = "top-center",
            interval = null,
        }: Omit<ShowNotificationOptions, "color">) => {
            addNotification({
                message,
                title,
                color: ComponentColor.Info,
                position,
                interval,
            });
        },
        [addNotification],
    );

    return {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };
};

interface ShowNotificationOptions {
    message: string;
    title?: string;
    color?: Extract<ComponentColor, "info" | "success" | "error" | "warning">;
    position?: NotificatonPosition;
    interval?: number | null;
}
